// src/features/feedback/submit.ts
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from '../../lib/firebase';
import { t as uiText } from '../../i18n';
import { collectFeedbackContext } from './context';
import {
  FEEDBACK_COLLECTION,
  FEEDBACK_LIMITS,
  FEEDBACK_STORAGE_ROOT,
  categoryNeedsExpectedActual,
  type FeedbackAttachment,
  type FeedbackCategory,
  type FeedbackFile,
} from './types';

export type SubmitFeedbackInput = {
  category: FeedbackCategory;
  title: string;
  body: string;
  expected: string;
  actual: string;
  files: FeedbackFile[];
  workspaceId: string | null;
};

export class FeedbackValidationError extends Error {}

function safeFileName(name: string, index: number): string {
  return name.replace(/[^\w.-]+/g, '_').slice(0, 120) || `file-${index}.png`;
}

export async function submitFeedback(input: SubmitFeedbackInput): Promise<string> {
  const user = auth.currentUser;
  if (!user?.uid || !user.email) {
    throw new FeedbackValidationError(uiText("Login required"));
  }

  const title = input.title.trim();
  const body = input.body.trim();
  const withDetails = categoryNeedsExpectedActual(input.category);
  const expected = withDetails ? input.expected.trim() : '';
  const actual = withDetails ? input.actual.trim() : '';

  if (!title || !body) {
    throw new FeedbackValidationError(uiText("Enter a subject and details."));
  }
  if (
    title.length > FEEDBACK_LIMITS.titleMax ||
    body.length > FEEDBACK_LIMITS.bodyMax ||
    expected.length > FEEDBACK_LIMITS.expectedMax ||
    actual.length > FEEDBACK_LIMITS.actualMax
  ) {
    throw new FeedbackValidationError(uiText("The text is too long."));
  }
  if (input.files.length > FEEDBACK_LIMITS.attachmentsMax) {
    throw new FeedbackValidationError(
      uiText("You can attach up to {{max}} images.", { max: FEEDBACK_LIMITS.attachmentsMax })
    );
  }

  const context = collectFeedbackContext(input.workspaceId);
  const feedbackRef = doc(collection(db, FEEDBACK_COLLECTION));

  // Storage ルールは Firestore ドキュメントの存在を見ないので、先にアップロードしてから作成する
  const attachments: FeedbackAttachment[] = [];
  for (const [index, file] of input.files.entries()) {
    const contentType = file.blob.type || 'image/png';
    const path = `${FEEDBACK_STORAGE_ROOT}/${user.uid}/${feedbackRef.id}/${Date.now()}_${index}_${safeFileName(file.filename, index)}`;
    await uploadBytes(storageRef(storage, path), file.blob, { contentType });
    attachments.push({ path, contentType, size: file.blob.size });
  }

  const reporterName = (user.displayName || '').trim() || user.email;

  await setDoc(feedbackRef, {
    category: input.category,
    title,
    body,
    ...(expected ? { expected } : {}),
    ...(actual ? { actual } : {}),
    status: 'open',
    reporterUid: user.uid,
    reporterEmail: user.email,
    reporterName: reporterName.slice(0, 200),
    ...context,
    attachments,
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return feedbackRef.id;
}
